import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Input, Row, Select, Space, Statistic, Switch, Table, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { getNotifications, getNotificationSummary } from '../../api/notifications';

const pushStatusMap = {
  PENDING: { label: '待推送', color: 'orange' },
  SUCCESS: { label: '推送成功', color: 'green' },
  FAILED: { label: '推送失败', color: 'red' },
};

const notificationTypeMap = {
  REBATE_ARRIVED: '返利到账',
  MEMBERSHIP_EXPIRE: '会员提醒',
  WITHDRAW_SUCCESS: '提现结果',
};

const Notifications = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilters = {
    userId: searchParams.get('userId') || '',
    notificationType: searchParams.get('notificationType') || '',
    pushStatus: searchParams.get('pushStatus') || '',
    unreadOnly: searchParams.get('unreadOnly') === 'true',
  };

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const currentPage = pagination.current;
  const currentPageSize = pagination.pageSize;

  const fetchSummary = async () => {
    setSummaryLoading(true);
    try {
      const result = await getNotificationSummary();
      setSummary(result);
    } catch (error) {
      console.error('Failed to fetch notification summary:', error);
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getNotifications({
        page: currentPage,
        size: currentPageSize,
        userId: appliedFilters.userId || undefined,
        notificationType: appliedFilters.notificationType || undefined,
        pushStatus: appliedFilters.pushStatus || undefined,
        unreadOnly: appliedFilters.unreadOnly || undefined,
      });
      setData(result.list);
      setPagination((prev) => ({ ...prev, total: result.total }));
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, currentPageSize, appliedFilters]);

  useEffect(() => {
    fetchSummary();
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => {
    const nextFilters = { ...filters };
    setAppliedFilters(nextFilters);
    setPagination((prev) => ({ ...prev, current: 1 }));

    const params = {};
    if (nextFilters.userId) {
      params.userId = nextFilters.userId;
    }
    if (nextFilters.notificationType) {
      params.notificationType = nextFilters.notificationType;
    }
    if (nextFilters.pushStatus) {
      params.pushStatus = nextFilters.pushStatus;
    }
    if (nextFilters.unreadOnly) {
      params.unreadOnly = 'true';
    }
    setSearchParams(params);
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    {
      title: '用户',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/users?userId=${record.userId}`)}>
            {record.userNickname || `用户 ${record.userId}`}
          </Button>
          <span style={{ color: '#999' }}>ID: {record.userId}</span>
        </Space>
      ),
    },
    {
      title: '通知类型',
      dataIndex: 'notificationType',
      render: (value) => notificationTypeMap[value] || value,
    },
    { title: '标题', dataIndex: 'title', width: 180 },
    { title: '内容', dataIndex: 'content', ellipsis: true },
    {
      title: '已读状态',
      dataIndex: 'read',
      render: (value) => <Tag color={value ? 'default' : 'blue'}>{value ? '已读' : '未读'}</Tag>,
    },
    {
      title: '推送状态',
      dataIndex: 'pushStatus',
      render: (value) => {
        const item = pushStatusMap[value] || { label: value, color: 'default' };
        return <Tag color={item.color}>{item.label}</Tag>;
      },
    },
    {
      title: '推送时间',
      dataIndex: 'pushTime',
      render: (value) => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
      width: 160,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      render: (value) => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
      width: 160,
    },
  ];

  return (
    <div>
      <h2>通知运营</h2>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card loading={summaryLoading}>
            <Statistic title="通知总数" value={summary?.totalCount || 0} />
          </Card>
        </Col>
        <Col span={6}>
          <Card loading={summaryLoading}>
            <Statistic title="未读通知" value={summary?.unreadCount || 0} valueStyle={{ color: '#ff2442' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card loading={summaryLoading}>
            <Statistic title="推送失败" value={summary?.failedCount || 0} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card loading={summaryLoading}>
            <Statistic title="今日新增" value={summary?.todayCount || 0} valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
      </Row>

      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          placeholder="用户ID"
          value={filters.userId}
          onChange={(e) => setFilters((prev) => ({ ...prev, userId: e.target.value }))}
          onPressEnter={handleSearch}
          style={{ width: 140 }}
        />
        <Select
          placeholder="通知类型"
          allowClear
          style={{ width: 160 }}
          value={filters.notificationType || undefined}
          onChange={(value) => setFilters((prev) => ({ ...prev, notificationType: value || '' }))}
          options={[
            { value: 'REBATE_ARRIVED', label: '返利到账' },
            { value: 'MEMBERSHIP_EXPIRE', label: '会员提醒' },
            { value: 'WITHDRAW_SUCCESS', label: '提现结果' },
          ]}
        />
        <Select
          placeholder="推送状态"
          allowClear
          style={{ width: 140 }}
          value={filters.pushStatus || undefined}
          onChange={(value) => setFilters((prev) => ({ ...prev, pushStatus: value || '' }))}
          options={[
            { value: 'PENDING', label: '待推送' },
            { value: 'SUCCESS', label: '推送成功' },
            { value: 'FAILED', label: '推送失败' },
          ]}
        />
        <Space>
          <span>仅看未读</span>
          <Switch
            checked={filters.unreadOnly}
            onChange={(checked) => setFilters((prev) => ({ ...prev, unreadOnly: checked }))}
          />
        </Space>
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
          搜索
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1500 }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, pageSize) => setPagination({ current: page, pageSize, total: pagination.total }),
        }}
      />
    </div>
  );
};

export default Notifications;
